import { useFormik } from "formik";
import {
  Button,
  Divider,
  Stack,
  Typography,
  TextField,
  Box,
  Alert,
  FormControl,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { CreateMonitorInput } from "../mutations";
import { MonitoringEditFormSchema } from "./schema";
import MonitoringIcon from "./monitoring-icon.svg";
import { Monitor } from "../../fixtures";

export type FormState = ReturnType<
  typeof MonitoringEditFormSchema.validateSync
>;

export interface Props {
  monitor: Monitor;
  onCancel: () => void;
  onSuccess: (domain: string) => void;
  onError: (domain: string, reason?: string) => void;
  onEditMonitor: (formData: CreateMonitorInput) => Promise<void>;
}

export default function MonitorCreateForm({
  monitor,
  onCancel,
  onSuccess,
  onError,
  onEditMonitor,
}: Props) {
  const formik = useFormik({
    initialValues: {
      merchantName: monitor.merchantName || "",
      merchantDescription: monitor.merchantDescription || "",
      merchantMcc: monitor.merchantMcc || "",
      externalReferenceId: monitor.externalReferenceId || "",
      emailAddress: monitor.emailAddress || "",
      phoneNumber: monitor.phoneNumber || "",
      address: monitor.address || {
        line1: "",
        line2: "",
        city: "",
        stateProvince: "",
        postalCode: "",
        country: "",
      },
    },
    onSubmit: async (values: FormState, { resetForm }) => {
      try {
        await onEditMonitor(values as any);
        resetForm();
        onSuccess(monitor.domain);
      } catch (e) {
        onError(monitor.domain, e ? `${e}` : "");
      }
    },
    validationSchema: MonitoringEditFormSchema,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack
        sx={{
          alignItems: "center",
          mb: "2em",
        }}
      >
        <img src={MonitoringIcon} width={100} />
        <Typography align="center">
          Update TrueBiz
          <br />
          merchant monitoring
        </Typography>
      </Stack>
      <Stack
        sx={{
          gap: "1em",
        }}
      >
        <FormControl>
          <TextField fullWidth disabled value={monitor.domain} />
        </FormControl>

        <FormControl>
          <TextField
            fullWidth
            name="merchantName"
            label="Merchant Name"
            onChange={formik.handleChange}
            value={formik.values.merchantName}
            error={
              !!(formik.touched.merchantName && formik.errors.merchantName)
            }
          />
          {formik.errors.merchantName && formik.touched.merchantName && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {formik.errors.merchantName}
            </Alert>
          )}
        </FormControl>

        <FormControl>
          <TextField
            fullWidth
            name="merchantDescription"
            label="Merchant Description"
            onChange={formik.handleChange}
            value={formik.values.merchantDescription}
            error={
              !!(
                formik.touched.merchantDescription &&
                formik.errors.merchantDescription
              )
            }
          />
          {formik.errors.merchantDescription &&
            formik.touched.merchantDescription && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {formik.errors.merchantDescription}
              </Alert>
            )}
        </FormControl>

        {/* TODO: make this a searchable dropdown w descriptions */}
        <FormControl>
          <TextField
            fullWidth
            name="merchantMcc"
            label="Merchant MCC (required)"
            required
            onChange={formik.handleChange}
            value={formik.values.merchantMcc}
            error={!!(formik.touched.merchantMcc && formik.errors.merchantMcc)}
          />
          {formik.errors.merchantMcc && formik.touched.merchantMcc && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {formik.errors.merchantMcc}
            </Alert>
          )}
        </FormControl>

        <FormControl>
          <TextField
            fullWidth
            name="externalReferenceId"
            label="External Reference ID"
            onChange={formik.handleChange}
            value={formik.values.externalReferenceId}
            error={
              !!(
                formik.touched.externalReferenceId &&
                formik.errors.externalReferenceId
              )
            }
          />
          {formik.errors.externalReferenceId &&
            formik.touched.externalReferenceId && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {formik.errors.externalReferenceId}
              </Alert>
            )}
        </FormControl>

        <Divider />

        <FormControl>
          <TextField
            fullWidth
            name="emailAddress"
            label="Email Address"
            onChange={formik.handleChange}
            value={formik.values.emailAddress}
            error={
              !!(formik.touched.emailAddress && formik.errors.emailAddress)
            }
          />
          {formik.errors.emailAddress && formik.touched.emailAddress && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {formik.errors.emailAddress}
            </Alert>
          )}
        </FormControl>

        <FormControl>
          <TextField
            fullWidth
            name="phoneNumber"
            type="tel"
            label="Phone Number"
            onChange={formik.handleChange}
            value={formik.values.phoneNumber}
            error={!!(formik.touched.phoneNumber && formik.errors.phoneNumber)}
          />
          {formik.errors.phoneNumber && formik.touched.phoneNumber && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {formik.errors.phoneNumber}
            </Alert>
          )}
        </FormControl>

        <Divider />

        <FormControl>
          <TextField
            fullWidth
            name="address.line1"
            label="Address Line 1"
            onChange={formik.handleChange}
            value={formik.values.address?.line1}
            error={
              !!(
                (formik.touched.address as any)?.line1 &&
                (formik.errors.address as any)?.line1
              )
            }
          />
          {(formik.errors.address as any)?.line1 &&
            (formik.touched.address as any)?.line1 && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {(formik.errors.address as any)?.line1}
              </Alert>
            )}
        </FormControl>

        <FormControl>
          <TextField
            fullWidth
            name="address.line2"
            label="Address Line 2"
            onChange={formik.handleChange}
            value={formik.values.address?.line2}
            error={
              !!(
                (formik.touched.address as any)?.line2 &&
                (formik.errors.address as any)?.line2
              )
            }
          />
          {(formik.errors?.address as any)?.line2 &&
            (formik.touched?.address as any)?.line2 && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {(formik.errors?.address as any)?.line2}
              </Alert>
            )}
        </FormControl>

        <FormControl>
          <TextField
            name="address.city"
            label="City"
            onChange={formik.handleChange}
            value={formik.values.address?.city}
            error={
              !!(
                (formik.touched?.address as any)?.city &&
                (formik.errors?.address as any)?.city
              )
            }
          />
          {(formik.errors?.address as any)?.city &&
            (formik.touched?.address as any)?.city && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {(formik.errors?.address as any)?.city}
              </Alert>
            )}
        </FormControl>

        <Box
          sx={{
            display: "flex",
            gap: "1em",
            width: "100%",
          }}
        >
          <FormControl sx={{ display: "flex", flex: 1 }}>
            <TextField
              name="address.stateProvince"
              label="State/Province"
              onChange={formik.handleChange}
              value={formik.values.address?.stateProvince}
              error={
                !!(
                  (formik.touched.address as any)?.stateProvince &&
                  (formik.errors.address as any)?.stateProvince
                )
              }
            />
            {(formik.errors.address as any)?.stateProvince &&
              (formik.touched.address as any)?.stateProvince && (
                <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                  {(formik.errors.address as any)?.stateProvince}
                </Alert>
              )}
          </FormControl>

          <FormControl sx={{ display: "flex", flex: 1 }}>
            <TextField
              name="address.postalCode"
              label="Postal Code"
              onChange={formik.handleChange}
              value={formik.values.address?.postalCode}
              error={
                !!(
                  (formik.touched.address as any)?.postalCode &&
                  (formik.errors.address as any)?.postalCode
                )
              }
            />
            {(formik.errors.address as any)?.postalCode &&
              (formik.touched.address as any)?.postalCode && (
                <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                  {(formik.errors.address as any)?.postalCode}
                </Alert>
              )}
          </FormControl>
        </Box>

        <FormControl>
          <TextField
            name="address.country"
            label="Country"
            onChange={formik.handleChange}
            value={formik.values.address?.country}
            error={
              !!(
                (formik.touched.address as any)?.country &&
                (formik.errors.address as any)?.country
              )
            }
          />
          {(formik.errors.address as any)?.country &&
            (formik.touched.address as any)?.country && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {(formik.errors.address as any)?.country}
              </Alert>
            )}
        </FormControl>
      </Stack>
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          width: "100%",
          gap: "1.5em",
          marginTop: "1.5em",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            formik.resetForm();
            onCancel();
          }}
          disabled={formik.isSubmitting}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          type="submit"
          loading={formik.isSubmitting}
        >
          Modify Enrollment
        </LoadingButton>
      </div>
    </form>
  );
}
